import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

export function formatDate(dateString) {
  if (!dateString) return '-';
  try {
    const [year, month, day] = dateString.split('-');
    return `${day}/${month}/${year}`;
  } catch (e) {
    return dateString;
  }
}

export function formatDateBR(dateString) {
  return formatDate(dateString);
}

export function calculateDaysRemaining(dateString) {
  if (!dateString) return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateString);
  const diffTime = target - today;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
}

export function getUrgencyStatus(dateString) {
  if (!dateString) return 'normal';
  const days = calculateDaysRemaining(dateString);
  if (days < 0) return 'overdue';
  if (days <= 7) return 'urgent'; // 0-7 days
  return 'on-track'; // > 7 days
}

export function getCategoryForItem(item, categories) {
  if (!item.dueDate) return null;
  const days = calculateDaysRemaining(item.dueDate);
  
  // Sort categories by threshold ascending to find the tightest match
  // Filter out the "catch-all" (usually 0 or high number, depending on logic)
  // Logic: 
  // < 0: Overdue
  // 0-3: Atenção
  // 4-7: Próximo
  // > 7: Safe
  
  // Find "Vencida" specifically for negative days
  if (days < 0) {
    return categories.find(c => c.name === 'Vencida') || categories[0];
  }

  // Find matching positive thresholds
  // We want the smallest threshold that is greater than or equal to 'days'
  // e.g. days=2. Thresholds: 3 (Attention), 7 (Next). 3 >= 2. Match Attention.
  // e.g. days=5. Thresholds: 3, 7. 3 < 5. 7 >= 5. Match Next.
  
  const sorted = [...categories]
    .filter(c => c.name !== 'Vencida' && c.daysThreshold > 0)
    .sort((a, b) => a.daysThreshold - b.daysThreshold);
    
  for (const cat of sorted) {
    if (days <= cat.daysThreshold) {
      return cat;
    }
  }
  
  // Fallback for > max threshold (Green)
  return categories.find(c => c.name === 'Dentro do prazo') || categories[categories.length - 1];
}

export function getUrgencyTextColor(status) {
  switch (status) {
    case 'overdue': return 'text-red-600';
    case 'urgent': return 'text-yellow-600';
    case 'on-track': return 'text-green-600';
    default: return 'text-blue-600';
  }
}

export function getUrgencyBgColor(status) {
  switch (status) {
    case 'overdue': return 'bg-red-50';
    case 'urgent': return 'bg-yellow-50';
    case 'on-track': return 'bg-green-50';
    default: return 'bg-blue-50';
  }
}

export function getUrgencyBorderColor(status) {
  switch (status) {
    case 'overdue': return 'border-red-300';
    case 'urgent': return 'border-yellow-300';
    case 'on-track': return 'border-green-300';
    default: return 'border-blue-300';
  }
}

export function getStatusColor(status) {
  switch (status) {
    case 'Concluído':
    case 'completed': return 'text-green-600 bg-green-50 border-green-200';
    case 'Aberto':
    case 'open': return 'text-blue-600 bg-blue-50 border-blue-200';
    case 'in-progress': return 'text-orange-600 bg-orange-50 border-orange-200';
    case 'blocked':
    case 'overdue': return 'text-red-600 bg-red-50 border-red-200';
    case 'planning': return 'text-purple-600 bg-purple-50 border-purple-200';
    case 'testing': return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    default: return 'text-gray-600 bg-gray-50 border-gray-200';
  }
}

export function getStatusDotColor(status) {
  switch (status) {
    case 'Concluído':
    case 'completed': return 'text-green-600 border-green-600';
    case 'Aberto':
    case 'open': return 'text-blue-600 border-blue-600';
    case 'in-progress': return 'text-orange-600 border-orange-600';
    case 'blocked':
    case 'overdue': return 'text-red-600 border-red-600';
    case 'planning': return 'text-purple-600 border-purple-600';
    case 'testing': return 'text-yellow-600 border-yellow-600';
    default: return 'text-gray-600 border-gray-600';
  }
}

export function normalizeLifecycleStatus(status) {
  if (!status) return 'Sem Status';
  const normalized = String(status)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

  if (['concluido', 'completed', 'done', 'fechado', 'finalizado'].includes(normalized)) {
    return 'Concluído';
  }

  if (['aberto', 'open', 'planejamento', 'em_andamento', 'in-progress', 'in_progress', 'andamento'].includes(normalized)) {
    return 'Aberto';
  }

  return status;
}

export function getItemTypeLabel(type) {
  const map = {
    'goals': 'Metas',
    'services': 'Serviços',
    'attendance': 'Atendimentos',
    'implementations': 'Implementações'
  };
  return map[type] || type;
}

export function sortByColumn(items, column, direction = 'asc') {
  return [...items].sort((a, b) => {
    let valA = a[column];
    let valB = b[column];

    // Handle null/undefined
    if (valA === undefined || valA === null) valA = '';
    if (valB === undefined || valB === null) valB = '';

    // Handle string case insensitivity
    if (typeof valA === 'string') valA = valA.toLowerCase();
    if (typeof valB === 'string') valB = valB.toLowerCase();

    if (valA < valB) return direction === 'asc' ? -1 : 1;
    if (valA > valB) return direction === 'asc' ? 1 : -1;
    return 0;
  });
}

export function formatTelefone(value) {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  if (cleaned.length <= 10) {
    return cleaned.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
  }
  return cleaned.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3');
}

export function formatCNPJ(value) {
  if (!value) return '';
  const cleaned = value.replace(/\D/g, '');
  return cleaned.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
}